/**
 * Mega-Menü-Fallback aus der Tabelle categories (sort_order, parent_id).
 * Wird genutzt, wenn kein ASL-Kategoriebaum verfügbar ist (Kunden-Shops).
 * Zwei Ebenen: Hauptkategorien (Spalten-Köpfe) und deren Unterkategorien (Links).
 */
export const DB_KEY_PREFIX = 'db:';

const sortKey = (c) => Number(c.sortOrder ?? c.sort_order ?? 0);

export function buildDbCategoryTree(categories = []) {
  const list = (Array.isArray(categories) ? categories : []).filter(
    (c) => c && c.id != null && typeof c.slug === 'string' && c.slug && typeof c.name === 'string' && c.name,
  );
  const byParent = new Map();
  for (const c of list) {
    const parent = c.parentId ?? c.parent_id ?? null;
    if (!byParent.has(parent)) byParent.set(parent, []);
    byParent.get(parent).push(c);
  }
  const sorted = (parent) =>
    (byParent.get(parent) || []).slice().sort((a, b) => sortKey(a) - sortKey(b) || String(a.name).localeCompare(String(b.name), 'de'));

  const build = (parentId, level, slugPath) =>
    sorted(parentId).map((c) => {
      const slugs = [...slugPath, c.slug];
      return {
        key: `${DB_KEY_PREFIX}${c.id}`,
        name: c.name,
        slug: c.slug,
        level,
        url: `/catalog/${slugs.map((s) => encodeURIComponent(s)).join('/')}`,
        children: level < 2 ? build(c.id, level + 1, slugs) : [],
      };
    });

  return build(null, 1, []);
}

/**
 * DB-Kategorie-Slug -> Namen der ASL-Snapshot-Roots, deren Unterbäume darunter einsortiert werden.
 * PULSAR (ASL-Herstellerzweig) gehört fachlich zu Stromversorgung & Gehäusetechnik und ist kein eigener Root.
 */
export const DB_ROOT_ASL_SOURCES = {
  alarmtechnik: ['Gefahrenmeldetechnik'],
  videoueberwachung: ['Videoüberwachung'],
  brandmeldetechnik: ['Brandmeldetechnik'],
  zutrittskontrolle: ['Zutrittskontrolle'],
  'stromversorgung-infrastruktur': ['PULSAR', 'Stromversorgung', 'Stromversorgung & Gehäusetechnik'],
};

/**
 * Wählt die Menü-Wurzeln.
 * 1. Gibt es DB-Kategorien, definieren sie die Root-Menge (gleiche 6 Roots wie Startseite/Footer).
 *    Root-Links führen auf /catalog/<slug> (gleicher Zählweg wie der Katalog). ASL-Unterbäume
 *    werden als Untergruppen einsortiert, ASL-Roots erscheinen nie als Top-Level.
 * 2. Ohne DB-Kategorien: ASL-Baum, wenn geladen (ohne Sammelknoten asl:22903).
 */
export function resolveMenuRoots({ aslTree, aslStatus, dbCategories } = {}) {
  const dbRoots = buildDbCategoryTree(dbCategories);
  if (dbRoots.length > 0) {
    const aslReady = aslStatus === 'ready' && Array.isArray(aslTree) && aslTree.length > 0;
    return dbRoots.map((root) => {
      const sources = DB_ROOT_ASL_SOURCES[root.slug] || [];
      const aslChildren = aslReady
        ? aslTree
            .filter((node) => sources.includes(node.name) && node.key !== 'asl:22903')
            .flatMap((node) => node.children || [])
        : [];
      return { ...root, children: aslChildren.length ? aslChildren : root.children };
    });
  }
  if (aslStatus === 'ready' && Array.isArray(aslTree) && aslTree.length > 0) {
    return aslTree.filter((category) => category.key !== 'asl:22903');
  }
  return [];
}
